<template>
<section :class="{'has-pagination': pagination}" class="z-table">
  <section
    :style="showFixed ? '' : 'content-visibility:hidden;'"
    :class="{
      'has-children': hasChildren
    }"
    class="z-table-wrapper"
  >
    <section
      ref="tableBodyWrapper"
      class="z-table-body-wrapper"
      @scroll="onTableBodyWrapperScroll"
    >
      <table ref="mainTable" border :style="width ? `width:${width}` : ''">
        <colgroup>
          <col
            v-for="(col, colIndex) in allLeafCols"
            :key="colIndex"
            :style="Number.isFinite(col.width) ? `width:${col.width}px` : ''"
          />
        </colgroup>
        <thead>
        <tr
          v-for="(thRow, tRowIndex) in tHRows"
          :key="tRowIndex"
          ref="headRows"
        >
          <th
            v-for="(col, tColIndex) in thRow"
            :key="tColIndex"
            ref="thCells"
            :prop="col.prop"
            :rowspan="col.columns ? 1 : (tHRowNum - tRowIndex)"
            :colspan="col.leafColumns.length"
            :align="col.align"
          >
            <div
              :prop="col.prop"
              :style="Number.isFinite(col.width) ? `width:${col.width}px` : ''"
              class="z-table-cell"
            >{{col.label}}</div>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(row, rowIndex) in filterData"
          :key="rowIndex"
          ref="bodyRows"
          :class="{
            'hover-row': row === hoverWrapper.row,
            'selected-row': row === selectedWrapper.row,
            'expand': showChildrenMap[row[rowKey]]
          }"
          @mouseover="onRowHover(row)"
          @mouseout="onRowMouseout(row)"
          @click="onRowClick(row, rowIndex)"
          @dblclick="onRowDblclick(row, rowIndex)"
        >
          <td
            v-for="(col, colIndex) in allLeafCols"
            :key="colIndex"
            :align="col.align"
            :style="colIndex === 0 && rowDeepMap[row[rowKey]] ? `padding-left:${10 + (rowDeepMap[row[rowKey]]) * 16}px` : ''"
          >
            <span
              v-if="colIndex === 0 && Array.isArray(row.children) && row.children.length > 0"
              class="expand-btn"
              @click.stop="onExpandBtnClick(row)"
            ></span>
            <slot
              v-if="$scopedSlots[col.prop]"
              :name="col.prop"
              :row="row"
              :rowIndex="rowIndex"
            />
            <template v-else>{{row[col.prop]}}</template>
          </td>
        </tr>
        </tbody>
      </table>
    </section>

    <section
      ref="tableHeaderWrapper"
      v-show="showFixed"
      :style="{
        width: `${wrapperWidth}px`
      }"
      class="z-table-header-wrapper"
      @mousewheel.prevent="onTableHeaderWrapperMouseWheel"
    >
      <table
        border
        :width="tableWidth"
      >
        <colgroup>
          <col
            v-for="(col, colIndex) in allLeafCols"
            :key="colIndex"
            :width="colWidthMap[col.prop]"
          />
        </colgroup>
        <thead>
        <tr
          v-for="(thRow, tRowIndex) in tHRows"
          :key="tRowIndex"
          :style="{
            height: `${headRowHeights[tRowIndex]}px`
          }"
        >
          <th
            v-for="(col, tColIndex) in thRow"
            :key="tColIndex"
            :rowspan="col.columns ? 1 : (tHRowNum - tRowIndex)"
            :colspan="col.leafColumns.length"
            :align="col.align"
          >
            <div class="z-table-cell">{{col.label}}</div>
          </th>
        </tr>
        </thead>
      </table>
    </section>

    <section
      v-if="hasFixedCols"
      ref="fixedTableBodyWrapper"
      v-show="showFixed"
      :style="{
        height: `${wrapperHeight}px`
      }"
      class="z-table-fixed-body-wrapper"
      @mousewheel.prevent="onFixedTableBodyWrapperMouseWheel"
    >
      <table border :width="fixedTableWidth">
        <colgroup>
          <col
            v-for="(col, colIndex) in fixedLeafCols"
            :key="colIndex"
            :width="colWidthMap[col.prop]"
          />
        </colgroup>
        <thead>
        <tr
          v-for="(thRow, tRowIndex) in fixedTHRows"
          :key="tRowIndex"
          :style="{
            height: `${headRowHeights[tRowIndex]}px`
          }"
        >
          <th
            v-for="(col, tColIndex) in thRow"
            :key="tColIndex"
            :rowspan="col.columns ? 1 : (tHRowNum - tRowIndex)"
            :colspan="col.leafColumns.length"
            :align="col.align"
          >
            <div class="z-table-cell">{{col.label}}</div>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(row, rowIndex) in filterData"
          :key="rowIndex"
          :style="{
            height: `${bodyRowHeights[rowIndex]}px`
          }"
          :class="{
            'hover-row': row === hoverWrapper.row,
            'selected-row': row === selectedWrapper.row,
            'expand': showChildrenMap[row[rowKey]]
          }"
          @mouseover="onRowHover(row)"
          @mouseout="onRowMouseout(row)"
          @click="onRowClick(row, rowIndex)"
          @dblclick="onRowDblclick(row, rowIndex)"
        >
          <td
            v-for="(col, colIndex) in fixedLeafCols"
            :key="colIndex"
            :align="col.align"
            :style="colIndex === 0 && rowDeepMap[row[rowKey]] ? `padding-left:${10 + (rowDeepMap[row[rowKey]]) * 16}px` : ''"
          >
            <span
              v-if="colIndex === 0 && Array.isArray(row.children) && row.children.length > 0"
              class="expand-btn"
              @click.stop="onExpandBtnClick(row)"
            ></span>
            <slot
              v-if="$scopedSlots[col.prop]"
              :name="col.prop"
              :row="row"
              :rowIndex="rowIndex"
            />
            <template v-else>{{row[col.prop]}}</template>
          </td>
        </tr>
        </tbody>
      </table>
    </section>

    <section
      v-if="hasFixedCols"
      v-show="showFixed"
      class="z-table-fixed-header-wrapper"
      @mousewheel.prevent="onFixedTableHeaderMouseWheel"
    >
      <table border :width="fixedTableWidth">
        <colgroup>
          <col
            v-for="(col, colIndex) in fixedLeafCols"
            :key="colIndex"
            :width="colWidthMap[col.prop]"
          />
        </colgroup>
        <thead>
        <tr
          v-for="(thRow, tRowIndex) in fixedTHRows"
          :key="tRowIndex"
          :style="{
            height: `${headRowHeights[tRowIndex]}px`
          }"
        >
          <th
            v-for="(col, tColIndex) in thRow"
            :key="tColIndex"
            :rowspan="col.columns ? 1 : (tHRowNum - tRowIndex)"
            :colspan="col.leafColumns.length"
            :align="col.align"
          >
            <div class="z-table-cell">{{col.label}}</div>
          </th>
        </tr>
        </thead>
      </table>
    </section>
  </section>

  <section v-if="pagination" class="z-pagination-wrapper">
    <section class="z-pagination-content">
      <span style="color:#606266;">共<span style="color:#409EFF"> {{data.length}} </span>条</span>
      <ul class="z-page-no-list">
        <li
          :disabled="paginationConfig.no === 1"
          class="z-page-no"
          @click="onSwitchPageBtnClick(-1)"
        >&lt;</li>
        <li
          v-for="pageNo in paginationConfig.pageNos"
          :key="pageNo"
          :class="{selected: paginationConfig.no === pageNo}"
          class="z-page-no"
          @click="onPageNoBtnClick(pageNo)"
        >{{pageNo}}</li>
        <li
          :disabled="paginationConfig.no === paginationConfig.totalPage"
          class="z-page-no"
          @click="onSwitchPageBtnClick(1)"
        >&gt;</li>
      </ul>
    </section>
  </section>
</section>
</template>

<script>
import Pagination from './ZTablePagination'
import Column from './ZTableColumn'

export default {
  name: 'ZTable',
  props: {
    columns: {
      required: true,
      type: Array
    },
    data: {
      required: true,
      type: Array
    },
    width: {
      type: String
    },
    rowKey: {},
    selectedRowStyle: {
      type: Boolean,
      default: false
    },
    section: {
      type: Object
    },
    pagination: {
      type: Pagination
    }
  },
  data () {
    return {
      showFixed: true,
      wrapperWidth: 0,
      wrapperHeight: 0,
      tableWidth: 0,
      colWidthMap: Object.freeze({}),
      headRowHeights: Object.freeze([]),
      bodyRowHeights: Object.freeze([]),
      rowDeepMap: {},
      parentKeyMap: {},
      showChildrenMap: {},
      hoverWrapper: Object.freeze({}),
      selectedWrapper: Object.freeze({})
    }
  },
  computed: {
    tCols () {
      return this.columns.map(column => new Column(column))
    },
    fixedTCols () {
      return this.tCols.filter(col => col.fixed)
    },
    hasChildren () {
      return Object.values(this.rowDeepMap).length > 0
    },
    tHRowNum () {
      return Math.max(...this.tCols.map(col => col.rDeep))
    },
    tHRows () {
      return this.getTHRows(this.tCols)
    },
    fixedTHRows () {
      return this.getTHRows(this.fixedTCols)
    },
    allLeafCols () {
      return this.getLeafCols(this.tCols)
    },
    fixedLeafCols () {
      return this.getLeafCols(this.fixedTCols)
    },
    fixedTableWidth () {
      let {colWidthMap} = this
      return this.fixedLeafCols.reduce((sum, col) => sum + colWidthMap[col.prop], 0)
    },
    hasFixedCols () {
      return this.fixedTCols.length > 0
    },
    paginationData () {
      let {data} = this

      if (!Array.isArray(data)) {
        return []
      }

      let {pagination} = this

      if (pagination) {
        let {size} = pagination
        let index = ((pagination.no - 1) * size)
        data = data.slice(index, index + size)
      }

      return data
    },
    filterData () {
      let {rowKey, paginationData} = this

      if (rowKey) {
        let {showChildrenMap} = this
        paginationData = [...paginationData]

        for (let i = 0; i < paginationData.length; i++) {
          let row = paginationData[i]

          if (showChildrenMap[row[rowKey]]) {
            paginationData.splice(i + 1, 0, ...row.children)
          }
        }
      }

      return paginationData
    },
    paginationConfig () {
      let {pagination: {no, size}} = this
      let totalPage = Math.ceil(this.data.length / size)
      let pageNos = []
      let start = Math.max(no - 4, 1)
      let end = Math.min(start + 8, totalPage)

      start = Math.max(end - 8, 1)

      for (let i = start; i <= end; i++) {
        pageNos.push(i)
      }

      return {
        no,
        size,
        totalPage,
        pageNos
      }
    }
  },
  methods: {
    setCurrentRow (row) {
      this.selectedWrapper = Object.freeze({
        row
      })
    },
    getTHRows (tCols) {
      let {tHRowNum} = this
      let tHRows = [tCols]

      for (let i = 1; i < tHRowNum; i++) {
        tHRows.push(tCols = tCols.reduce((row, {columns}) => {
          if (columns) {
            row.push(...columns)
          }

          return row
        }, []))
      }

      return tHRows
    },
    getLeafCols (tCols) {
      return tCols.reduce((colProps, tCol) => {
        colProps.push(...tCol.leafColumns)
        return colProps
      }, [])
    },
    setTreeRowKeyMap () {
      let {rowKey} = this
      let rowDeepMap = {}
      let parentKeyMap = {}
      let showChildrenMap = {}

      if (rowKey) {
        let {data} = this

        if (Array.isArray(data)) {
          let dataList = [...data]

          for (let i = 0; i < dataList.length; i++) {
            let data = dataList[i]
            let {children} = data
            let rowKeyVal = data[rowKey]
            let parentRowKeyDeep = rowDeepMap[parentKeyMap[rowKeyVal]]

            if (Array.isArray(children) && children.length > 0) {
              rowDeepMap[rowKeyVal] = parentRowKeyDeep ? (parentRowKeyDeep + 1) : 1
              showChildrenMap[rowKeyVal] = 0

              dataList.splice(i + 1, 0, ...children)

              children.forEach(data => {
                parentKeyMap[data[rowKey]] = rowKeyVal
              })
            } else if (parentRowKeyDeep) {
              rowDeepMap[rowKeyVal] = parentRowKeyDeep + 1
            }
          }
        }
      }

      this.parentKeyMap = Object.freeze(parentKeyMap)
      this.rowDeepMap = Object.freeze(rowDeepMap)
      this.showChildrenMap = showChildrenMap
    },
    doLayout () {
      let {$refs} = this
      let {tableBodyWrapper, mainTable, headRows, bodyRows} = $refs

      this.wrapperWidth = tableBodyWrapper.clientWidth
      this.wrapperHeight = tableBodyWrapper.clientHeight
      this.tableWidth = window.getComputedStyle(mainTable).width
      this.colWidthMap = Object.freeze($refs.thCells.reduce((map, cell) => {
        map[cell.getAttribute('prop')] = Number(window.getComputedStyle(cell).width.replace('px', ''))
        return map
      }, {}))
      this.headRowHeights = Object.freeze(headRows.map(row => row.offsetHeight))

      if (bodyRows) {
        this.bodyRowHeights = Object.freeze(bodyRows.map(row => row.offsetHeight))
      }
    },
    clearDoLayoutTimer () {
      clearTimeout(this.doLayoutTimer)
    },
    readyDoLayout (delay) {
      console.time('ZTable')
      this.clearDoLayoutTimer()

      this.doLayoutTimer = setTimeout(() => {
        this.showFixed = false
        this.$nextTick(() => {
          this.doLayout()

          this.$nextTick(() => {
            this.showFixed = true
            console.timeEnd('ZTable')
          })
        })
      }, delay)
    },
    onWindowResize () {
      this.readyDoLayout(100)
    },
    clearScrollStatusTimer () {
      clearTimeout(this.scrollStatusTimer)
    },
    setScrollStatus () {
      this.clearScrollStatusTimer()

      this.scrolling = true

      this.scrollStatusTimer = setTimeout(() => {
        this.scrolling = false
      }, 300)
    },
    onTableBodyWrapperScroll (event) {
      let {$refs} = this
      let {tableBodyWrapper} = $refs

      $refs.tableHeaderWrapper.scrollLeft = tableBodyWrapper.scrollLeft

      if (this.fixedTCols.length > 0) {
        $refs.fixedTableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop
      }

      this.setScrollStatus()
    },
    onFixedTableBodyWrapperMouseWheel (event) {
      let {$refs} = this
      let {tableBodyWrapper} = $refs
      $refs.fixedTableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop + event.deltaY
      this.setScrollStatus()
    },
    onTableHeaderWrapperMouseWheel (event) {
      let {$refs} = this
      let {tableBodyWrapper} = $refs
      $refs.tableHeaderWrapper.scrollLeft = tableBodyWrapper.scrollLeft = tableBodyWrapper.scrollLeft + event.deltaX
      this.setScrollStatus()
    },
    onFixedTableHeaderMouseWheel (event) {
      let {$refs} = this
      let {tableBodyWrapper} = $refs
      $refs.fixedTableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop + event.deltaY
      $refs.tableHeaderWrapper.scrollLeft = tableBodyWrapper.scrollLeft = tableBodyWrapper.scrollLeft + event.deltaX
      this.setScrollStatus()
    },
    onRowHover (row) {
      if (this.scrolling) {
        return
      }

      this.hoverWrapper = Object.freeze({
        row
      })
      this.$emit('row-hover', row)
    },
    onRowMouseout (row) {
      if (this.scrolling) {
        return
      }

      this.hoverWrapper = Object.freeze({})
      this.$emit('row-mouseout', row)
    },
    setRowSelectStyle (row) {
      if (this.selectedRowStyle) {
        if (this.selectedWrapper.row !== row) {
          this.selectedWrapper = Object.freeze({
            row
          })

          return true
        } else {
          this.selectedWrapper = Object.freeze({})
        }
      }

      return false
    },
    onRowClick (row) {
      this.$emit('row-click', row, this.setRowSelectStyle(row))
    },
    onRowDblclick (row) {
      this.setRowSelectStyle(row)
      this.$emit('row-dblclick', row)
    },
    onExpandBtnClick (row) {
      let rowKeyVal = row[this.rowKey]
      let {showChildrenMap} = this
      showChildrenMap[rowKeyVal] = !showChildrenMap[rowKeyVal]
      this.readyDoLayout(0)
    },
    onPageNoBtnClick (pageNo) {
      let {pagination} = this

      if (pagination.no === pageNo) {
        return
      }

      pagination.no = pageNo

      let {$refs} = this
      $refs.tableBodyWrapper.scrollTop = 0

      if (this.hasFixedCols) {
        $refs.fixedTableBodyWrapper.scrollTop = 0
      }

      this.$emit('pagination-no-changed', pageNo)
      this.readyDoLayout(0)
    },
    onSwitchPageBtnClick (direction) {
      let pageNo = this.pagination.no + direction

      if (pageNo < 1 || pageNo > this.paginationConfig.totalPage) {
        return
      }

      this.onPageNoBtnClick(pageNo)
    }
  },
  watch: {
    tCols: {
      immediate: true,
      handler (newVal) {
        this.readyDoLayout(0)
      }
    },
    data: {
      immediate: true,
      handler (newVal) {
        this.setTreeRowKeyMap()
      }
    },
    filterData: {
      immediate: true,
      handler (newVal) {
        this.readyDoLayout(0)
      }
    }
  },
  created () {
    window.addEventListener('resize', this.onWindowResize, true)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onWindowResize, true)
    this.clearDoLayoutTimer()
    this.clearScrollStatusTimer()
  }
}
</script>

<style lang="scss">
.z-table {
  $PAGINATION-HEIGHT: 40px;

  position: relative;
  width: 100%;
  height: 100%;

  &.has-pagination {
    padding-bottom: $PAGINATION-HEIGHT;
  }

  table {
    table-layout: fixed;
    border-collapse: collapse;
    border-spacing: 0;
    border: 0;
  }

  .z-pagination-wrapper {
    position: absolute;
    font-size: 14px;
    bottom: 0;
    left: 0;
    width: 100%;
    height: $PAGINATION-HEIGHT;
    padding-top: 10px;
  }
}

.z-table-wrapper {
  $BORDER-COLOR: #EBEEF5;

  position: relative;
  font-size: .875rem;
  width: 100%;
  height: 100%;
  border: 1px solid $BORDER-COLOR;
  /*content-visibility: hidden;*/
  content-visibility: auto;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;

  &.has-children {
    td:first-child {
      padding-left: 26px;
    }

    tr {
      &.expand {
        .expand-btn {
          &:before {
            transform: rotate(135deg);
          }
        }
      }
    }

    .expand-btn {
      display: inline-block;
      float: left;
      width: 16px;
      height: 22px;
      margin-left: -26px;
      padding: 8px 0 8px 10px;
      text-align: center;
      cursor: pointer;

      &:hover {
        &:before {
          border-color: #409EFF;
        }
      }

      &:before {
        content: "";
        display: block;
        width: 100%;
        height: 100%;
        border: {
          top: 1px solid #909399;
          right: 1px solid #909399;
        };
        transform: rotate(45deg);
      }
    }
  }

  > .z-table-header-wrapper,
  > .z-table-fixed-body-wrapper,
  > .z-table-fixed-header-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    overflow: hidden;
  }

  > .z-table-header-wrapper,
  > .z-table-fixed-header-wrapper {
    border-bottom: 1px solid $BORDER-COLOR;
    background-color: #f9f9f9;

    table {
      margin-bottom: -1px;
    }
  }

  > .z-table-body-wrapper,
  > .z-table-fixed-body-wrapper {
    table {
      background-color: #ffffff;
    }

    tbody {
      tr {
        &:nth-child(2n) {
          background-color: #fafafa;
        }

        &.hover-row {
          background-color: #f5f7fa;
        }

        &.selected-row {
          background-color: #ecf5ff;
        }
      }
    }
  }

  > .z-table-body-wrapper {
    width: 100%;
    height: 100%;
    overflow: auto;
  }

  .z-table-cell,
  td {
    min-width: 50px;
    padding: 6px 10px;
    line-height: 1.6428571428571428;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-all;
    overflow: hidden;
  }

  table {
    border-color: $BORDER-COLOR;
  }

  tr {
    cursor: default;
  }

  th {
    color: #303030;
  }

  td {
    color: #606266;
  }
}

.z-pagination-wrapper {
  background-color: #ffffff;

  .z-pagination-content {
    float: right;
    height: 100%;
    line-height: 30px;
  }

  .z-page-no-list {
    float: right;
    height: 100%;
    margin: 0 0 0 1em;
    padding: 0;
    font-weight: 500;
    user-select: none;

    .z-page-no {
      float: left;
      width: 30px;
      margin: 0 1px;
      text-align: center;
      color: #606266;
      background-color: #f4f4f5;
      cursor: pointer;

      &.selected {
        color: #ffffff;
        background-color: #409eff;
      }

      &[disabled="disabled"] {
        color: #c0c4cc;
        cursor: not-allowed;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ZTable/index.vue